import _ from 'lodash';
import type * as StackbitTypes from '@stackbit/types';
import type { SitemapBranch, SitemapEntry } from '../../reducers/studio';
import type { CollectionObject, LoadingField, ObjectsCollection } from '../../reducers/studio/fields';
import type { RootFieldModel, FieldModel, ListFieldItemsModel } from '../../types/FieldModel';
import type { Schema, ReferenceMap, ObjectDefinition, ModelDefinition, ReferenceObject, ObjectSchemaDefinition, ContentSource } from '../../types/Schema';
import type { Document, FieldPath, Field, ListField, RootDocument, BackendFieldNonLocalized, BackendDocument, BackendField } from '../../types/Field';
import type { PathMap } from '../../types/dom-types';
import AppError from '../../lib/AppError';
import { iterateRootField } from './iterate-fields';

export function findSitemapEntry(node: SitemapBranch, cb: (item: SitemapEntry) => boolean): SitemapEntry | undefined {
    return Object.values(node).find((item) => {
        if (cb(item)) {
            return true;
        }
        return findSitemapEntry(item.children, cb);
    });
}

export function getObjectsCollectionByObjectId(collections: ObjectsCollection[], object: ObjectDefinition): ObjectsCollection | null {
    return (
        collections.find(
            (collection) =>
                collection.srcType === object.srcType && collection.srcProjectId === object.srcProjectId && findObjectInList(collection?.objects, object)
        ) ?? null
    );
}

export function getObjectInCollection(collections: ObjectsCollection[], object: ObjectDefinition): CollectionObject | null {
    return getObjectsCollectionByObjectId(collections, object)?.objects?.find(({ srcObjectId }) => srcObjectId === object.srcObjectId) ?? null;
}

export function getCollectionSchema(
    collection: { srcType: string; srcProjectId: string; srcModelName: string } | null,
    schema: Schema | null
): RootFieldModel | undefined {
    if (!collection || !schema) {
        return undefined;
    }
    return schema[collection.srcType]?.[collection.srcProjectId]?.[collection.srcModelName];
}

/**
 * Go over all objects on the page and construct a map of all references between parents and children.
 */
export const getReferenceMap = (fields: RootDocument[], pathMap: PathMap): ReferenceMap => {
    const references: ReferenceMap = {};
    iterateRootField(fields, (field, fieldPath, data) => {
        if (field.type === 'unresolved_reference' || field.type === 'cross-reference') {
            if (!references[field.refId]) {
                references[field.refId] = {};
            }
            const curObjectId = fieldPath[0] as string;
            if (!references[field.refId]![curObjectId]) {
                references[field.refId]![curObjectId] = [];
            }

            const elements = pathMap[fieldPathToString([field.refId])];

            let xpath: string | undefined;

            const parentXpath = getNearestXpath(fieldPath, pathMap);
            if (parentXpath) {
                const elementsRanged = _.flatMap(elements, (element) => {
                    if (!element.xpath.startsWith(parentXpath)) {
                        return [];
                    }
                    const commonXpath = getCommonParts(element.xpath, parentXpath).join('');
                    return [
                        {
                            ...element,
                            commonXpath,
                            commonXpathLength: commonXpath.length
                        }
                    ];
                });
                const maxSameXpath = _.maxBy(elementsRanged, 'commonXpathLength');
                const filteredRanged = elementsRanged?.filter((element) => element.commonXpathLength === maxSameXpath?.commonXpathLength);

                const fieldName = fieldPath[fieldPath.length - 1];

                if (filteredRanged?.length === 1) {
                    xpath = filteredRanged[0]!.xpath;
                } else if (typeof fieldName === 'number') {
                    const elementsInList = elements?.filter((element) => element.xpath.startsWith(parentXpath));

                    if (elementsInList?.length === filteredRanged?.length) {
                        const elementsIndexesInList =
                            (data.parentField as ListField | undefined)?.items.reduce((arr, item, index) => {
                                if ((item.type === 'unresolved_reference' || item.type === 'cross-reference') && item.refId === field.refId) {
                                    arr.push(index);
                                }
                                return arr;
                            }, [] as number[]) ?? [];
                        const curIndex = elementsIndexesInList.indexOf(fieldName);
                        xpath = elementsInList?.[curIndex]?.xpath;
                    }
                }
            }

            references[field.refId]![curObjectId]!.push({
                fieldPath,
                xpath
            });
        }
    });
    return references;
};

/**
 * Given a field path, and optionally an xpath, find the chain of ancestor objects that reference this object until no single parent can be found.
 * Use referenceMap and pathMap to resolve parents and refine results
 */
export const getAncestorsForFieldPath = ({
    fieldPath,
    referenceMap,
    xpath,
    pathMap
}: {
    fieldPath: FieldPath;
    referenceMap: ReferenceMap;
    xpath?: string;
    pathMap?: PathMap;
}): FieldPath[] => {
    if (!fieldPath.length) {
        return [];
    }

    const ancestors: FieldPath[] = [fieldPath];
    const visited: string[] = [];
    let referenceParent = resolveSingleParent({ objectId: fieldPath[0] as string, referenceMap, xpath, pathMap });
    while (referenceParent && !visited.includes(referenceParent[0] as string)) {
        ancestors.unshift(referenceParent);
        visited.push(referenceParent[0] as string);
        referenceParent = resolveSingleParent({ objectId: referenceParent[0] as string, referenceMap, xpath, pathMap });
    }

    return ancestors;
};

/**
 * getChildReferences -
 * Receives an object and a list of all local objects,
 * and returns a list containing the object itself and all objects that were referred by it recursively.
 */
export const getChildReferences = (object: Document, allObjects: Document[]): Document[] => {
    if (!object) {
        return [];
    }

    const objects: Document[] = [object];
    iterateRootField(objects, (field) => {
        if (field.type === 'unresolved_reference' || field.type === 'cross-reference') {
            if (objects.find((obj) => obj.srcObjectId === field.refId)) {
                return false;
            }
            const nextObj = allObjects.find((obj) => obj.srcObjectId === field.refId);
            if (nextObj) {
                objects.push(...getChildReferences(nextObj, allObjects));
            }
        }
        return undefined;
    });
    return objects;
};

/**
 * Given a single objectId, resolve it's single parent using referenceMap and xpath.
 * If more than one parent is found and they cannot be filtered by xpath, return undefined.
 */
export const resolveSingleParent = ({
    objectId,
    referenceMap,
    xpath,
    pathMap
}: {
    objectId: string;
    referenceMap: ReferenceMap;
    xpath?: string;
    pathMap?: PathMap;
}): FieldPath | undefined => {
    const parents = referenceMap[objectId];
    const parentsIds = Object.keys(parents ?? {});
    if (!parentsIds?.length) {
        return undefined;
    }

    if (!xpath || !pathMap) {
        if (parentsIds.length === 1) {
            return parents![parentsIds[0]!]?.[0]?.fieldPath;
        }
        return undefined;
    }

    // more than one, resolve using xpaths

    const flatParents = _.chain(parents).cloneDeep().values().flatten().value();

    const directParents = flatParents.filter((parent) => parent.xpath === xpath);
    if (directParents.length === 1) {
        return directParents[0]!.fieldPath;
    } else if (directParents.length > 1) {
        // in case there are multiple parents with same xpath - remove it so code below will use getNearestXpath to filter parents using parents containers
        directParents.forEach((parent) => {
            parent.xpath = undefined;
        });
    }

    const flatParentsMapped = flatParents.map((field) => {
        const curXpath = field.xpath ?? getNearestXpath(field.fieldPath, pathMap, { targetXpath: xpath }) ?? '';
        let commonXpath = getCommonParts(xpath, curXpath).join('');
        if (commonXpath.endsWith('/')) {
            commonXpath = commonXpath.slice(0, -1);
        }
        return {
            ...field,
            commonXpath: commonXpath,
            xpathMatch: commonXpath.length
        };
    });

    const maxXpath = _.maxBy(flatParentsMapped, 'xpathMatch');
    const parentsSameXpath = flatParentsMapped.filter((field) => field.xpathMatch === maxXpath?.xpathMatch);

    if (parentsSameXpath.length === 1) {
        return parentsSameXpath[0]!.fieldPath;
    }

    // more than 1 with the same xpath means to be same object in one array typically
    const containerXpath = parentsSameXpath[0]!.commonXpath;
    const objectAllMap = pathMap[fieldPathToString([objectId])];
    if (!objectAllMap) {
        return undefined;
    }

    const objectMap = objectAllMap.filter((element) => element.xpath.startsWith(containerXpath));

    if (objectMap.length !== parentsSameXpath.length) {
        return undefined;
    }

    const currentXpathIndex = objectMap.findIndex((obj) => obj.xpath === xpath);
    return parentsSameXpath[currentXpathIndex]?.fieldPath;
};

export const getNearestXpath = (
    fieldPath: FieldPath,
    pathMap: PathMap,
    { targetXpath, returnFirst }: { targetXpath?: string; returnFirst?: boolean } = {}
): string | undefined => {
    const processFieldPath: FieldPath = [...fieldPath];

    do {
        const map = pathMap[fieldPathToString(processFieldPath)];
        processFieldPath.splice(-2);

        if (!map?.length) {
            continue;
        }

        if (targetXpath) {
            const xpath = _.maxBy(map, (element) => getCommonParts(targetXpath, element.xpath))?.xpath;
            if (xpath) {
                return xpath;
            }
        } else {
            if (returnFirst || map.length === 1) {
                return map[0]!.xpath;
            }
        }
    } while (processFieldPath.length);

    return undefined;
};

export const getLinkKey = (field: BackendDocument | BackendField | RootDocument | Field): string => {
    let linkKey = '';
    if (field?.type === 'list') {
        linkKey = 'items';
    } else if (['object', 'image', 'document', 'reference', 'cross-reference', 'model'].includes(field.type)) {
        linkKey = 'fields';
    }
    return linkKey;
};

export type RootObjectUsage = Record<string, ReferenceObject>;

export const getRootObjectsForReferencedObject = ({
    srcObjectId,
    srcProjectId,
    srcType,
    objects,
    schema,
    result = {},
    stats = {
        added: 0
    },
    usage = 0,
    visited = [],
    onlyPages,
    limit
}: ObjectDefinition & {
    objects: CollectionObject[];
    schema: Schema;
    result?: RootObjectUsage;
    usage?: number;
    onlyPages?: boolean;
    visited?: string[];
    limit?: number;
    stats?: { added: number };
}): RootObjectUsage => {
    const object = findObjectInList(objects, { srcObjectId, srcProjectId, srcType });
    const visitedId = `${srcType}:${srcProjectId}:${srcObjectId}`;
    if (!object || visited.includes(visitedId)) {
        return result;
    }
    visited.push(visitedId);
    const model = schema?.[object.srcType]?.[object.srcProjectId]?.[object.srcModelName];
    const parents = object.referencedBy ?? [];
    const isPage = model?.type === 'page';
    if (!parents.length || isPage) {
        const shouldTrack = onlyPages ? isPage : true;
        if (shouldTrack) {
            const defaultCount = !usage && isPage ? 1 : 0;
            result[object.srcObjectId] = result[object.srcObjectId] || {
                srcObjectId: object.srcObjectId,
                srcType: object.srcType,
                srcProjectId: object.srcProjectId,
                srcObjectLabel: object.srcObjectLabel,
                count: defaultCount
            };
            result[object.srcObjectId]!.count += usage;
            stats.added += 1;
            if (limit && stats.added >= limit) {
                return result;
            }
        }
    }

    _.forEach(parents, (parentObject) => {
        if (limit && stats.added >= limit) {
            return false;
        }
        getRootObjectsForReferencedObject({
            srcObjectId: parentObject.srcDocumentId,
            srcType: parentObject.srcType,
            srcProjectId: parentObject.srcProjectId,
            objects,
            schema,
            result,
            onlyPages,
            visited,
            limit,
            stats,
            usage: Math.max(parentObject.count, usage)
        });
        return true;
    });

    return result;
};

export type ReferencedObjectUsage = {
    rootSrcObjectId: string;
    rootSrcType: string;
    rootSrcProjectId: string;
    totalUsages: number;
    objects: ReferenceObject[];
};

export const getReferencesObjectsForObject = ({
    srcObjectId,
    srcProjectId,
    srcType,
    objects,
    schema,
    result = [],
    onlyPages,
    rootObject,
    usages = 1,
    deepLevel = 1,
    visited = [],
    limit,
    stats = {
        total: 0
    }
}: ObjectDefinition & {
    objects: CollectionObject[];
    schema: Schema;
    result?: ReferencedObjectUsage[];
    onlyPages?: boolean;
    rootObject?: ReferencedObjectUsage;
    usages?: number;
    deepLevel?: number;
    visited?: string[];
    limit?: number;
    stats?: { total: number };
}): ReferencedObjectUsage[] => {
    const object = findObjectInList(objects, { srcObjectId, srcProjectId, srcType });
    const visitedId = `${srcType}:${srcProjectId}:${srcObjectId}`;
    if (!object || visited.includes(visitedId)) {
        return result;
    }
    visited.push(visitedId);
    if (rootObject) {
        if (rootObject.objects.length >= deepLevel) {
            // multiple parents - create new root object
            rootObject = _.cloneDeep(rootObject);
            rootObject.objects = _.takeRight(rootObject.objects, deepLevel - 1);
            rootObject.totalUsages = rootObject.objects.reduce((res, object) => {
                if (object.count > 1) {
                    res += object.count - 1;
                }
                return res;
            }, 1);
            stats.total += rootObject.totalUsages - 1;
            result.push(rootObject);
        }
        rootObject.rootSrcObjectId = object.srcObjectId;
        rootObject.objects.unshift({
            srcObjectId: object.srcObjectId,
            srcType: object.srcType,
            srcProjectId: object.srcProjectId,
            srcObjectLabel: object.srcObjectLabel,
            count: usages
        });
        if (usages > 1) {
            rootObject.totalUsages += usages - 1;
            stats.total += usages - 1;
        }
    } else {
        rootObject = {
            rootSrcObjectId: srcObjectId,
            rootSrcType: srcType,
            rootSrcProjectId: srcProjectId,
            totalUsages: usages,
            objects: [
                {
                    srcObjectLabel: object.srcObjectLabel,
                    srcObjectId: object.srcObjectId,
                    srcType: object.srcType,
                    srcProjectId: object.srcProjectId,
                    count: usages
                }
            ]
        };
        stats.total += rootObject.totalUsages - 1;
        result.push(rootObject);
    }

    const parents = object.referencedBy ?? [];
    const model = schema?.[object.srcType]?.[object.srcProjectId]?.[object.srcModelName];
    if (!parents.length) {
        // dont store root level objects if they are not linked anywhere
        let shouldKeepObject = true;
        if (onlyPages) {
            shouldKeepObject = model?.type === 'page';
        } else {
            shouldKeepObject = deepLevel === 1 ? parents.length > 0 : true;
        }
        if (!shouldKeepObject) {
            const index = result.indexOf(rootObject);
            result.splice(index, 1);
        }
        return result;
    }

    if (limit && stats.total > limit) {
        return result;
    }

    _.forEach(parents, (parentObject) => {
        getReferencesObjectsForObject({
            srcObjectId: parentObject.srcDocumentId,
            srcType: parentObject.srcType,
            srcProjectId: parentObject.srcProjectId,
            objects,
            schema,
            result,
            onlyPages,
            rootObject,
            visited,
            usages: parentObject.count,
            deepLevel: deepLevel + 1,
            limit,
            stats
        });
        if (limit && stats.total > limit) {
            return false;
        }
        return true;
    });

    return result;
};

export const findFieldObjectIndexInList = (list: (RootDocument | LoadingField)[], seek: ObjectDefinition | undefined | null) =>
    seek
        ? list.findIndex((object) => object.srcType === seek.srcType && object.srcProjectId === seek.srcProjectId && object.srcObjectId === seek.srcObjectId)
        : -1;

export const findFieldObjectInList = <T extends RootDocument | LoadingField, U extends ObjectDefinition>(
    list: T[],
    seek: U | undefined | null
): RootDocument | LoadingField | undefined => list[findFieldObjectIndexInList(list, seek)];

export const findObjectIndexInList = (list: ObjectDefinition[], seek: ObjectDefinition | undefined | null) =>
    seek
        ? list.findIndex((object) => object.srcType === seek.srcType && object.srcProjectId === seek.srcProjectId && object.srcObjectId === seek.srcObjectId)
        : -1;

export const findObjectInList = <T extends ObjectDefinition, U extends ObjectDefinition>(list: T[], seek: U | undefined | null) =>
    list[findObjectIndexInList(list, seek)];

export const findModelIndexInList = (list: ModelDefinition[], seek: ModelDefinition | undefined | null) =>
    seek ? list.findIndex((object) => object.srcType === seek.srcType && object.srcProjectId === seek.srcProjectId && object.name === seek.name) : -1;

export const findModelInList = <T extends ModelDefinition, U extends ModelDefinition>(list: T[], seek: U | undefined | null) =>
    list[findModelIndexInList(list, seek)];

export const findCollectionIndexInList = (list: ObjectSchemaDefinition[], seek: ModelDefinition | undefined | null) =>
    seek ? list.findIndex((object) => object.srcType === seek.srcType && object.srcProjectId === seek.srcProjectId && object.srcModelName === seek.name) : -1;

export const findCollectionInList = <T extends ObjectSchemaDefinition, U extends ModelDefinition>(list: T[], seek: U | undefined | null) =>
    list[findCollectionIndexInList(list, seek)];

export const findSourceIndexInList = (list: ContentSource[], seek: ContentSource | undefined | null) =>
    seek ? list.findIndex((object) => object.srcType === seek.srcType && object.srcProjectId === seek.srcProjectId) : -1;

export const findSourceInList = <T extends ContentSource, U extends ContentSource>(list: T[], seek: U | undefined | null) =>
    list[findSourceIndexInList(list, seek)];

export const isSameObjects = (...objects: Array<ObjectDefinition | null | undefined>) => {
    const target = objects[0];
    return objects
        .slice(1)
        .every((object) => object?.srcType === target?.srcType && object?.srcProjectId === target?.srcProjectId && object?.srcObjectId === target?.srcObjectId);
};

export const isSameModels = (...models: ModelDefinition[]) => {
    const target = models[0];
    return models.slice(1).every((model) => model?.srcType === target?.srcType && model?.srcProjectId === target?.srcProjectId && model?.name === target?.name);
};

export const fieldPathToString = (fieldPath?: FieldPath) =>
    _.reduce(
        fieldPath,
        (accumulator, fieldName, index) => {
            if (_.isString(fieldName) && /\W/.test(fieldName)) {
                // field name is a string with non alphanumeric character
                accumulator += `['${fieldName}']`;
            } else if (_.isNumber(fieldName)) {
                accumulator += `[${fieldName}]`;
            } else {
                if (index > 0) {
                    accumulator += '.';
                }
                accumulator += fieldName;
            }
            return accumulator;
        },
        ''
    );

// TODO: deprecate and use dataFieldPathToFieldPathDocument after converting objects to documents
export const dataFieldPathToFieldPath = (document: RootDocument, dataFieldPath: FieldPath) => {
    const fieldPath: FieldPath = [document.srcObjectId];
    let currentField: RootDocument | Field = document;

    _.forEach(dataFieldPath, (fieldNameOrIndex) => {
        let nextField: Field | undefined;

        if (currentField.type === 'object' || currentField.type === 'image') {
            nextField = currentField.fields?.[fieldNameOrIndex as keyof (typeof currentField)['fields']];
        } else if (currentField.type === 'list') {
            nextField = currentField.items[fieldNameOrIndex as number];
        }

        if (nextField) {
            fieldPath.push(getLinkKey(currentField), fieldNameOrIndex);
            currentField = nextField;
        } else {
            return false;
        }

        return true;
    });

    return fieldPath;
};

export const fieldPathToDataFieldPath = (fieldPath: FieldPath) => fieldPath.filter((_, index) => index >= 2 && index % 2 === 0);

export const getCommonParts = (arr1: string, arr2: string) => {
    const res = [];
    let i = 0;
    while (i < arr1.length && i < arr2.length && arr1[i] === arr2[i]) {
        res.push(arr1[i]!);
        i++;
    }
    return res;
};

export const getLoadedFields = <T extends RootDocument | BackendDocument>(
    fields: Record<string, T | LoadingField | undefined> | Array<T | LoadingField | undefined>
): T[] => Object.values(fields).filter((field) => field && !('isLoading' in field)) as T[];

export function getFieldFromFieldPath(
    fields: StackbitTypes.Document[],
    fieldPath: FieldPath
): StackbitTypes.Document | StackbitTypes.Field | StackbitTypes.DocumentListFieldItems | undefined;
export function getFieldFromFieldPath(fields: BackendDocument[], fieldPath: FieldPath): BackendDocument | BackendField | undefined;
export function getFieldFromFieldPath(fields: RootDocument[], fieldPath: FieldPath): RootDocument | Field | undefined;
export function getFieldFromFieldPath(fields: $TSFixMe[], fieldPath: FieldPath) {
    const document = getRootDocumentFromFieldPath(fields, fieldPath);
    if (fieldPath.length === 1) {
        return document;
    }
    const fieldPathTail = _.tail(fieldPath);
    if (fieldPathTail.length) {
        return _.get(document, fieldPathTail);
    }
    return document;
}

export const getRootDocument = <T extends BackendDocument | RootDocument>(fields: T[], documentId: string): T | undefined =>
    fields.find((field) => field.srcObjectId === documentId);

export function getRootDocumentFromFieldPath<T extends BackendDocument | RootDocument | StackbitTypes.Document>(fields: T[], fieldPath: FieldPath) {
    const documentId = fieldPath[0];
    if (typeof documentId !== 'string') {
        return undefined;
    }
    return fields.find((field) => {
        if ('srcObjectId' in field) {
            return field.srcObjectId === documentId;
        }
        return field.id === documentId;
    });
}

export const getParentFieldPath = (fieldPath: FieldPath, sliceIndex = 2): FieldPath => fieldPath.slice(0, -1 * sliceIndex) as FieldPath;

export const isFieldDeletable = ({
    fields,
    schemaModels,
    fieldPath,
    localizedField
}: {
    fields: RootDocument[];
    schemaModels: RootFieldModel[];
    fieldPath: FieldPath;
    localizedField?: BackendFieldNonLocalized;
}) => {
    const parentObjectFieldPath = getParentFieldPath(fieldPath);
    const parentObject = getFieldFromFieldPath(fields, parentObjectFieldPath);
    const object = localizedField ?? getFieldFromFieldPath(fields, fieldPath);
    let removable = parentObject?.type === 'list' || object?.type === 'image';

    if (!removable) {
        if (object?.type === 'object' || object?.type === 'unresolved_reference' || object?.type === 'reference' || object?.type === 'cross-reference') {
            const isUnset = object.isUnset;
            if (!isUnset) {
                const rootField = getRootDocumentFromFieldPath(fields, fieldPath);
                const parentFieldModel =
                    rootField &&
                    getFieldModelFromFieldPath({
                        fields,
                        fieldPath: parentObjectFieldPath,
                        srcType: rootField.srcType,
                        srcProjectId: rootField.srcProjectId,
                        schemaModels
                    });

                if (parentFieldModel && 'fields' in parentFieldModel) {
                    const fieldModelFromParent = parentFieldModel?.fields?.find((fieldModel) => fieldModel.name === fieldPath[fieldPath.length - 1]);
                    removable = ['reference', 'cross-reference', 'object', 'model', 'image'].includes(fieldModelFromParent?.type ?? '');
                }
            }
        }
    }

    return removable;
};

const resolveFieldModelName = (field: RootDocument | Field) => {
    let resolvedModelName;
    if ((field.type === 'object' || field.type === 'image') && 'srcModelName' in field) {
        resolvedModelName = field.srcModelName;
    }
    return resolvedModelName;
};

export const getFieldModelFromFieldPath = (opts: {
    fields: RootDocument[];
    fieldPath: FieldPath;
    schemaModels: RootFieldModel[];
    srcType: string;
    srcProjectId: string;
    dontResolveLeafModel?: boolean;
    model?: RootFieldModel | FieldModel | ListFieldItemsModel;
    object?: RootDocument | Field;
}): RootFieldModel | FieldModel | ListFieldItemsModel | undefined => {
    const { object, fields, fieldPath, model, schemaModels, srcType, srcProjectId, dontResolveLeafModel } = opts;

    if (!fieldPath.length) {
        return model;
    }

    const fieldName = fieldPath[0]!;

    let field: RootDocument | Field | undefined;
    let fieldModel: RootFieldModel | FieldModel | ListFieldItemsModel | undefined;

    if (!object && typeof fieldName === 'string') {
        const rootField = getRootDocument(fields, fieldName);
        fieldModel =
            rootField &&
            findModelInList(schemaModels, {
                srcType: rootField.srcType,
                srcProjectId: rootField.srcProjectId,
                name: rootField.srcModelName
            });
        field = rootField;
    } else if (
        (model?.type === 'object' || model?.type === 'page' || model?.type === 'data' || model?.type === 'image' || model?.type === null) &&
        (object?.type === 'object' || object?.type === 'image')
    ) {
        if (object.type === 'object') {
            field = object.fields?.[fieldName];
        } else if (object.type === 'image' && (fieldName === 'title' || fieldName === 'url')) {
            field = object.fields?.[fieldName];
        }
        if ('fields' in model) {
            fieldModel = model.fields?.find((currentFieldModel) => currentFieldModel.name === fieldName);
        }
    } else if (model?.type === 'list' && object?.type === 'list' && typeof fieldName === 'number') {
        field = object.items[fieldName];
        fieldModel = model.items;
    }

    if (!fieldModel) {
        throw new AppError('Field model is undefined', { data: { object, fieldPath, model, schemaModels } });
    }

    const fieldPathTail = _.tail(fieldPath).slice(1);
    if (dontResolveLeafModel && fieldPathTail.length === 0) {
        return fieldModel;
    }

    if (field?.isUnset && fieldPathTail.length) {
        // srcModelName not set for isUnset objects
        throw new AppError(`Field is unset, can't proceed with getting field model for child field path`, {
            data: { object, fieldPath, model, schemaModels }
        });
    }

    if (!field) {
        throw new AppError(`Field is not resolved`);
    }

    const targetFieldData = getTargetModelFromFieldModel({
        models: schemaModels,
        field,
        fields,
        model: fieldModel,
        srcProjectId,
        srcType
    });
    fieldModel = targetFieldData.model ?? fieldModel;

    if (fieldPathTail.length) {
        return getFieldModelFromFieldPath({
            fields,
            object: targetFieldData.field,
            fieldPath: fieldPathTail,
            model: fieldModel,
            schemaModels,
            srcType,
            srcProjectId,
            dontResolveLeafModel
        });
    }

    return fieldModel;
};

export const getTargetModelFromFieldModel = ({
    model,
    field,
    fields,
    models,
    srcType,
    srcProjectId
}: {
    model: RootFieldModel | FieldModel | ListFieldItemsModel;
    field: RootDocument | Field;
    fields: RootDocument[];
    models: RootFieldModel[];
    srcType: string;
    srcProjectId: string;
}) => {
    let resolvedField: RootDocument | Field | undefined = field;
    if (field.type === 'unresolved_reference' || field.type === 'cross-reference') {
        resolvedField = findObjectInList(fields, {
            srcType: field.type === 'cross-reference' ? field.refSrcType : srcType,
            srcProjectId: field.type === 'cross-reference' ? field.refProjectId : srcProjectId,
            srcObjectId: field.refId
        });
    }
    let fieldModel: RootFieldModel | FieldModel | ListFieldItemsModel | undefined = model;

    if (model?.type === 'model') {
        if (model.models.length === 1) {
            fieldModel = findModelInList(models, { name: model.models[0]!, srcProjectId, srcType });
        }
        if (!field?.isUnset) {
            const resolvedModelName = resolvedField && resolveFieldModelName(resolvedField);
            fieldModel = resolvedModelName ? findModelInList(models, { name: resolvedModelName, srcProjectId, srcType }) : undefined;
        }
    } else if ((model.type === 'reference' || model.type === 'cross-reference') && !field.isUnset) {
        if (model.type === 'cross-reference' && field.type === 'cross-reference') {
            fieldModel = findModelInList(models, { name: field.refModelName, srcProjectId: field.refProjectId, srcType: field.refSrcType });
        } else if (model.type === 'reference') {
            const resolvedModelName = resolvedField && resolveFieldModelName(resolvedField);
            fieldModel = resolvedModelName ? findModelInList(models, { name: resolvedModelName, srcProjectId, srcType }) : undefined;
        }
    }

    return { model: fieldModel, field: resolvedField };
};

export const getAllFieldModelsFromFieldPath = ({
    fields,
    fieldPath,
    schemaModels,
    srcType,
    srcProjectId
}: {
    fields: RootDocument[];
    fieldPath: FieldPath;
    schemaModels: RootFieldModel[];
    srcType: string;
    srcProjectId: string;
}) => {
    const result: Record<
        string,
        {
            field: RootDocument | Field;
            resolvedField: RootDocument | Field | undefined;
            fieldModel: RootFieldModel | FieldModel | ListFieldItemsModel;
            resolvedModel: RootFieldModel | FieldModel | ListFieldItemsModel;
            fieldPath: FieldPath;
        }
    > = {};

    let parentField: RootDocument | Field | undefined;
    let parentFieldModel: RootFieldModel | FieldModel | ListFieldItemsModel | undefined;

    let currentFieldPath: FieldPath | undefined;
    let field: RootDocument | Field | undefined;
    let fieldModel: RootFieldModel | FieldModel | ListFieldItemsModel | undefined;
    do {
        currentFieldPath = currentFieldPath ? [...currentFieldPath, ...fieldPath.slice(currentFieldPath.length, currentFieldPath.length + 2)] : [fieldPath[0]!];
        field = getFieldFromFieldPath(fields, currentFieldPath);

        fieldModel = getFieldModelFromFieldPath({
            schemaModels,
            fields,
            srcType,
            srcProjectId,
            fieldPath: [currentFieldPath[currentFieldPath.length - 1]!],
            object: parentField,
            model: parentFieldModel,
            dontResolveLeafModel: true
        });

        if (!fieldModel || !field) {
            throw new AppError('Cant resolve field or fieldModel', { data: { fieldPath: currentFieldPath } });
        }

        const targetFieldData = getTargetModelFromFieldModel({
            field,
            fields,
            model: fieldModel,
            models: schemaModels,
            srcProjectId,
            srcType
        });

        if (!targetFieldData.model) {
            throw new AppError('Cant resolve fieldModel', { data: { fieldPath: currentFieldPath } });
        }

        result[fieldPathToString(currentFieldPath)] = {
            fieldPath: currentFieldPath,
            field,
            fieldModel,
            resolvedField: targetFieldData.field,
            resolvedModel: targetFieldData.model
        };

        parentField = targetFieldData.field;
        parentFieldModel = targetFieldData.model;
    } while (currentFieldPath.length !== fieldPath.length);

    return result;
};

export const stringToFieldPath = (fieldPathStr: string): FieldPath => {
    let isReadingBracket = false;
    let isReadingNumber = false;
    let currentValue = '';

    const lastIndex = fieldPathStr.length - 1;

    return _.reduce(
        fieldPathStr,
        (res, symbol, index) => {
            if (symbol === '[' || symbol === ']' || (symbol === '.' && !isReadingBracket) || index === lastIndex) {
                if (index === lastIndex && symbol !== ']' && symbol !== '.') {
                    currentValue += symbol;
                }
                if (currentValue) {
                    isReadingBracket = false;
                    const newValue = isReadingNumber ? parseInt(currentValue, 10) : currentValue;
                    res.push(newValue);
                    currentValue = '';
                }
                if (symbol === ']') {
                    isReadingNumber = false;
                    return res;
                }
            }

            if (symbol === '[') {
                isReadingBracket = true;
                isReadingNumber = true;
                return res;
            }

            if (symbol === '.') {
                if (isReadingBracket) {
                    currentValue += symbol;
                }
                return res;
            }

            if (isReadingBracket && symbol === `'`) {
                isReadingNumber = false;
                return res;
            }

            currentValue += symbol;

            return res;
        },
        [] as FieldPath
    );
};

export const toDocumentObjectDefinition = (object: ObjectDefinition) => ({
    srcType: object.srcType,
    srcProjectId: object.srcProjectId,
    srcDocumentId: object.srcObjectId
});

export const getDocumentSpecs = <T extends ObjectDefinition>(objects: T[]): StackbitTypes.DocumentSpecifier[] => objects.map(toDocumentObjectDefinition);
